import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { LIVE_TV_INITIAL_STATE, LiveTvState } from './live-tv.model';
import {
  createChannel,
  createLiveTvCategory,
  deleteCategory,
  deleteChannel,
  deleteChannelsMany,
  disableAllChannels,
  disableChannelsMany,
  editCategory,
  editChannelRow,
  enableAllChannels,
  enableChannelsMany,
  getLiveTvCategories,
  importInternetProvider,
  importM3u
} from '@shared/api/entertainment-api/live-tv-api';
import { CallStateEnum } from '@shared/models/call-state.model';
import useSnackbarStore from '@store/snackbar-store/snackbar.store';
import { withCallState } from '@shared/store-features/call-state-feature';
import { SnackbarType } from '@shared/models/app-snackbar.model';
import axios from 'axios';

const { showSnackbar } = useSnackbarStore.getState();

const useLiveTvStore = create<LiveTvState>()(
  devtools(
    (set, getState) => ({
      ...LIVE_TV_INITIAL_STATE,
      ...withCallState(getState),

      getCategoryList: async () => {
        try {
          const categoryData = await getLiveTvCategories();

          const categoryList = categoryData.map((item) => ({
            id: item._id,
            name: item.title,
            description: item.description
          }));

          set({
            categoryList
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createRow: async ({ data, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await createChannel(data);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_success_live_tv_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }

          throw error;
        }
      },
      editRow: async ({ data, state, handleClose, id }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await editChannelRow(data, id);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_update_live_tv_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },
      deleteRow: async ({ id, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          if (id instanceof Array) {
            await deleteChannelsMany(id);
          } else {
            await deleteChannel(id);
          }
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_delete_live_tv_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createCategory: async ({ data, handleClose }) => {
        set({ categoryCallState: CallStateEnum.Loading });
        try {
          await createLiveTvCategory(data);
          await getState().getCategoryList();
          set({ categoryCallState: CallStateEnum.Loaded });
          handleClose?.();
        } catch (error) {
          set({ categoryCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }

          throw error;
        }
      },

      editCategory: async ({ data, id, handleClose }) => {
        set({ categoryCallState: CallStateEnum.Loading });
        try {
          await editCategory(data as any, id);
          await getState().getCategoryList();
          set({ categoryCallState: CallStateEnum.Loaded });
          handleClose?.();
        } catch (error) {
          set({ categoryCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      deleteCategory: async ({ id, handleClose }) => {
        set({ categoryCallState: CallStateEnum.Loading });
        try {
          await deleteCategory(id);
          await getState().getCategoryList();
          set({ categoryCallState: CallStateEnum.Loaded });
          handleClose?.();
        } catch (error) {
          set({ categoryCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      importInternetProvider: async ({ data, handleClose, state }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await importInternetProvider(data);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_success_live_tv_import_internet_provider_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      importM3u: async ({ data, handleClose, state }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await importM3u(data);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_success_live_tv_import_m3e8_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      enableChannelsMany: async ({ ids, state, handleClose }) => {
        set({ channelStatusCallState: CallStateEnum.Loading });
        try {
          await enableChannelsMany(ids);
          set({ channelStatusCallState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
        } catch (error) {
          set({ channelStatusCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      disableChannelsMany: async ({ ids, state, handleClose }) => {
        set({ channelStatusCallState: CallStateEnum.Loading });
        try {
          await disableChannelsMany(ids);
          set({ channelStatusCallState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
        } catch (error) {
          set({ channelStatusCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      enableAllChannels: async ({ state, handleClose }) => {
        set({ channelStatusCallState: CallStateEnum.Loading });
        try {
          await enableAllChannels();
          set({ channelStatusCallState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
        } catch (error) {
          set({ channelStatusCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      disableAllChannels: async ({ state, handleClose }) => {
        set({ channelStatusCallState: CallStateEnum.Loading });
        try {
          await disableAllChannels();
          set({ channelStatusCallState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
        } catch (error) {
          set({ channelStatusCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      isChannelStatusLoading: () =>
        getState().channelStatusCallState === CallStateEnum.Loading,

      isCategoryLoading: () =>
        getState().categoryCallState === CallStateEnum.Loading,
      isCategoryLoaded: () =>
        getState().categoryCallState === CallStateEnum.Loaded,
      isCategoryError: () =>
        getState().categoryCallState === CallStateEnum.Error
    }),

    { name: 'LiveTvStore' }
  )
);

export default useLiveTvStore;
